"use client";

import React, { useState, useMemo } from "react";
import { SecuredFooter } from "../ui/secured-footer";
import { DPOS_FIGMA_ASSETS } from "./dposFigmaAssets";
import { formatZarNumberForDisplay } from "../../utils/zarDisplayFormat";

const KEYPAD_ROWS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  [".", "0", "backspace"],
];

interface AmountInputStepProps {
  onContinue: (amount: string) => void;
  onCancel: () => void;
}

export default function AmountInputStep({
  onContinue,
  onCancel,
}: AmountInputStepProps) {
  const [rawValue, setRawValue] = useState("");
  const [showValidationError, setShowValidationError] = useState(false);

  const formattedAmount = useMemo(() => {
    if (!rawValue) return "0.00";

    const hasDecimal = rawValue.includes(".");
    if (!hasDecimal) {
      return formatZarNumberForDisplay(Number(rawValue));
    }

    const [whole, decimal = ""] = rawValue.split(".");
    const formattedWhole = Number(whole || "0").toLocaleString("en-US", {
      maximumFractionDigits: 0,
    });
    return `${formattedWhole}.${decimal.padEnd(2, "0").slice(0, 2)}`;
  }, [rawValue]);

  const handleKeyPress = (key: string) => {
    setShowValidationError(false);

    if (key === "backspace") {
      setRawValue((prev) => prev.slice(0, -1));
      return;
    }

    if (key === ".") {
      if (rawValue.includes(".")) return;
      setRawValue((prev) => (prev === "" ? "0." : prev + "."));
      return;
    }

    // Limit decimal places to 2
    const decimalIndex = rawValue.indexOf(".");
    if (decimalIndex !== -1 && rawValue.length - decimalIndex > 2) return;

    // Limit whole number length
    if (!rawValue.includes(".") && rawValue.length >= 10) return;

    setRawValue((prev) => prev + key);
  };

  const numericAmount = parseFloat(rawValue || "0");
  const isValid = numericAmount > 0;
  const validationMessage =
    showValidationError && !isValid ? "Enter a valid amount" : null;

  const handleContinue = () => {
    if (!isValid) {
      setShowValidationError(true);
      return;
    }

    onContinue(rawValue);
  };

  return (
    <div className="bg-white rounded-[24px] flex flex-col items-center w-full min-h-[790px] relative">
      <div className="flex flex-1 flex-col gap-6 items-center justify-center pt-10 pb-20 w-full max-w-[414px] mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-6 items-center text-center w-full">
          <h2 className="text-[24px] font-semibold leading-[28px] text-[#33125F]">
            Amount Due
          </h2>
          <p className="text-[14px] font-normal leading-[20px] text-[#423E51]">
            Please enter the amount due
          </p>
        </div>

        {/* Amount Input */}
        <div className="flex flex-col gap-3 w-full">
          <p className="text-[14px] font-normal leading-[20px] text-[#6D6584]">
            Enter ZAR amount
          </p>
          <div
            className={`rounded-[16px] h-[88px] flex items-center px-6 ${
              validationMessage
                ? "border border-[#EB3366]"
                : "border border-[#CECBD7]"
            }`}
          >
            <div className="flex gap-3 items-center">
              <img
                src={DPOS_FIGMA_ASSETS.southAfricaFlag}
                alt="ZAR"
                className="w-10 h-10 rounded-full object-cover"
              />
              <span className="text-[24px] font-semibold leading-[32px] text-[#6D6584]">
                R {formattedAmount}
              </span>
            </div>
          </div>
          {validationMessage ? (
            <p className="text-[14px] font-normal leading-[20px] text-[#EB3366]">
              {validationMessage}
            </p>
          ) : null}
        </div>

        {/* Keypad */}
        <div className="flex flex-col gap-4 items-center">
          {KEYPAD_ROWS.map((row, rowIndex) => (
            <div key={rowIndex} className="flex gap-4">
              {row.map((key) => (
                <button
                  key={key}
                  onClick={() => handleKeyPress(key)}
                  className="w-[88px] h-[88px] rounded-full border border-[#CECBD7] flex items-center justify-center hover:bg-[#F5F4F7] active:bg-[#EBE9EF] transition-colors"
                >
                  {key === "backspace" ? (
                    <span className="text-[32px] font-semibold text-[#6D6584]">
                      {"<"}
                    </span>
                  ) : (
                    <span className="text-[32px] font-semibold text-[#6D6584]">
                      {key}
                    </span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex gap-4 items-center justify-center w-full">
          <button
            onClick={onCancel}
            className="border border-[#EBE9EF] rounded-full h-[48px] px-6 flex-1 flex items-center justify-center hover:bg-[#F5F4F7] transition-colors"
          >
            <span className="text-[14px] font-semibold text-[#6D6584]">
              Cancel Payment
            </span>
          </button>
          <button
            onClick={handleContinue}
            className={`rounded-full h-[48px] px-6 flex-1 flex items-center justify-center transition-opacity ${
              isValid
                ? "hover:opacity-90 cursor-pointer"
                : "opacity-100 cursor-pointer"
            }`}
            style={{
              backgroundColor: isValid ? undefined : "#F2EFF7",
              backgroundImage: isValid
                ? "linear-gradient(77deg, #57249C 10.66%, #EB3366 113.47%)"
                : undefined,
            }}
          >
            <span
              className="text-[14px] font-semibold"
              style={{ color: isValid ? "#FFFFFF" : "#B3ABC4" }}
            >
              Continue
            </span>
          </button>
        </div>
      </div>

      <SecuredFooter className="w-full p-6 justify-center border-t border-[#EBE9EF] mt-auto" />
    </div>
  );
}
